import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import toaster from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';
import { ActivateBusinessModal } from 'components/admin/businesses/ActivateBusinessModal';
import { LaundroworksIntegration } from 'components/admin/businesses/LaundroworksIntegration/LaundroworksIntegration.tsx';
import { CentsAssist } from 'components/admin/businesses/assist/CentsAssist';
import BusinessDevices from 'components/admin/businesses/devices/BusinessDevices';
import { GMVData } from 'components/admin/businesses/gmv/GMVData';
import { PayPalMerchantAccounts } from 'components/admin/businesses/paypalAccounts/MerchantAccounts';
import { MerchantAccounts } from 'components/admin/businesses/payrocAccounts/MerchantAccounts';
import { TwilioIntegration } from 'components/admin/businesses/twilioIntegration/TwilioIntegration';
import { WebhookEndpoints } from 'components/admin/businesses/webhooks/WebhookEndpoints';
import Modal from 'components/common/Modal';
import NotFound from 'components/common/NotFound/NotFound';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
// Components
import { CentsDataGrid } from 'components/common/dataGrid/dataGrid';
import DetailsCard from 'components/common/detailsCard/DetailsCard';
import IndividualValueEditForm from 'components/common/forms/IndividualValueEditForm';
import Tabs from 'components/common/tabsTable/TabsTable';
import { LIVELINK_ORDER_PAGE } from 'utils/config';
// constants
import {
  BUSINESS_MODAL_TYPES,
  BUSINESS_TYPE_VALUES,
  businessTypes,
  BUTTONS,
  THEME_ELEMENTS,
  THEME_MESSAGES,
} from 'utils/constants';
// Utils
import { getIsNotFound } from 'utils/getIsNotFound';
// API
import {
  acceptStripeTermsOfService,
  updateIndividualValueForBusiness,
  updateStatementDescriptor,
} from 'api/admin/businesses/businesses';
import { useArchivePayoutAccounts } from 'api/queries/useArchivePayoutAccounts';
import { useGeneratePredictiveInsightsForBusiness } from 'api/queries/useGeneratePredictiveInsightsforBusiness';
import { useImpersonateUser } from 'api/queries/useImpersonateUser';
import { useIndividualBusiness } from 'api/queries/useIndividualBusiness';
import { useMarkBusinessAsChurnReturn } from 'api/queries/useMarkBusinessAsChurnReturn';
import { useMarkBusinessAsChurned } from 'api/queries/useMarkBusinessAsChurned';
import { usePayoutAccountsForBusiness } from 'api/queries/usePayoutAccountsForBusiness';
import { useStoresForBusiness } from 'api/queries/useStoresForBusiness';
import { LwSubscriptionStatus, RecordSource } from 'constants/constants';
import EditMessengerConfiguration from './EditMessengerConfiguration/EditMessengerConfiguration';
import IndividualBusinessFeatureFlags from './IndividualBusinessFeatureFlags';
import IndividualBusinessSettings from './IndividualBusinessSettings';
import { BillingConfiguration } from './billing/BillingConfiguration';
import { EditDudaConfiguration } from './duda/EditDudaConfiguration';
import { Entitlements } from './entitlements/Entitlements';
import { ThemeContainer } from './themes/themeContainer';

const TABS_VALUES = {
  stores: 0,
  businessSettings: 1,
  featureFlags: 2,
  gmvData: 3,
  devices: 4,
  payoutAccounts: 5,
  payrocMerchantAccounts: 6,
  paypalMerchantAccounts: 7,
  webhookEndpoints: 8,
  centsAssist: 9,
  twilioIntegration: 10,
  billing: 11,
  laundroworksIntegration: 12,
  // Last on purpose: it is behind `entitlements-provisioning`, and tabs are addressed by index.
  entitlements: 13,
};

const TABS_DATA = [
  { name: 'Stores', value: 'stores' },
  { name: 'Business Settings', value: 'businessSettings' },
  { name: 'Feature Flags', value: 'featureFlags' },
  { name: 'GMV Data', value: 'gmvData' },
  { name: 'Devices', value: 'devices' },
  { name: 'Payout Accounts', value: 'payoutAccounts' },
  { name: 'Payroc Merchant Accounts', value: 'payrocMerchantAccounts' },
  { name: 'PayPal Merchant Accounts', value: 'paypalMerchantAccounts' },
  { name: 'Webhook Endpoints', value: 'webhookEndpoints' },
  { name: 'Cents Assist', value: 'centsAssist' },
  { name: 'Twilio integration', value: 'twilioIntegration' },
  { name: 'Billing', value: 'billing' },
  { name: 'Laundroworks Integration', value: 'laundroworksIntegration' },
  { name: 'Entitlements', value: 'entitlements' },
];

LaundroworksIntegration.propTypes = {
  businessId: PropTypes.number,
  businessName: PropTypes.string,
};
const IndividualBusinessProfile = () => {
  const { businessImpersonate, entitlementsProvisioning } = useFlags();
  const history = useHistory();
  const { id } = useParams();

  const {
    data: business,
    isLoading: isLoadingBusiness,
    error: businessError,
    refetch: refetchBusiness,
  } = useIndividualBusiness(id);

  const {
    data: storeList = [],
    isLoading: isLoadingStores,
    refetch: refetchStores,
  } = useStoresForBusiness(id, !!business);

  const {
    data: payoutAccounts = [],
    isLoading: isLoadingPayoutAccounts,
    refetch: refetchPayoutAccounts,
  } = usePayoutAccountsForBusiness(id, !!business);

  const isNotFound = businessError && getIsNotFound(businessError);

  const [modalType, setModalType] = useState(null);
  const [updatedFormValue, setUpdatedFormValue] = useState(null);
  const [updatedFormKey, setUpdatedFormKey] = useState(null);
  const [showBusinessDetailsModal, setShowBusinessDetailsModal] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [initialEditableCertainTheme, setInitialEditableCertainTheme] = useState(null);
  const [showBusinessTypeModal, setShowBusinessTypeModal] = useState(false);
  const [showEditMessengerConfigurationModal, setShowEditMessengerConfigurationModal] =
    useState(false);
  const [showDudaConfigurationModal, setShowDudaConfigurationModal] = useState(false);
  const [isActivateBusinessModalOpen, setIsActivateBusinessModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPayoutAccounts, setSelectedPayoutAccounts] = useState([]);
  const [selectedConnectAccountId, setSelectedConnectAccountId] = useState(null);
  const [acceptingTosAccountIds, setAcceptingTosAccountIds] = useState([]);
  const [generateInsights, setGenerateInsights] = useState(false);
  const [churnTypeConfirmed, setChurnTypeConfirmed] = useState(false);
  const [formError, setFormError] = useState(null);

  const loading = isLoadingBusiness || isLoadingStores || isLoadingPayoutAccounts;
  const error = businessError || formError;

  const selectedStoreData = useMemo(() => {
    return storeList.filter((store) => selectedStores.includes(store.id));
  }, [selectedStores]);

  const refetchAllData = async () => {
    await Promise.all([refetchBusiness(), refetchStores(), refetchPayoutAccounts()]);
  };

  const { isFetching: isFetchingPredictiveInsights, refetch: generatePredictiveInsights } =
    useGeneratePredictiveInsightsForBusiness({
      businessId: id,
      dataSharingConsentDate: business?.settings?.dataSharingConsentDate ?? null,
      isDataSharingConsented: business?.settings?.isDataSharingConsented ?? false,
      enabled: false,
    });

  const { mutateAsync: markBusinessAsChurned, isLoading: isMarkingBusinessAsChurned } =
    useMarkBusinessAsChurned({
      successCallback: refetchAllData,
    });

  const { mutateAsync: markBusinessAsChurnReturn, isLoading: isMarkingBusinessAsChurnReturn } =
    useMarkBusinessAsChurnReturn({
      successCallback: refetchAllData,
    });

  const { mutate: archivePayoutAccounts, isLoading: isArchivingPayoutAccounts } =
    useArchivePayoutAccounts({
      successCallback: refetchAllData,
    });

  const refreshStoreAndBusiness = async () => {
    setSelectedStores([]);
    setInitialEditableCertainTheme(null);
    await refetchAllData();
  };

  /**
   * Checking the entered value against the pattern
   *
   * @param {String} name
   * @param {Any} value
   */
  const checkIsValueValid = (value, name) => {
    const patterns = {
      'Stripe Statement Descriptor': /^(?=[^a-zA-Z]*[a-zA-Z])(?![^<>\\'"]*[<>\\'"])[ -~]{5,22}$/,
    };

    const error = `${name} value does not match the pattern.`;

    value && name in patterns && !patterns[name].test(value)
      ? setFormError(error)
      : setFormError(null);
  };

  /**
   * Save a key-value pair to the Business model
   *
   * @param {String} field
   * @param {Any} value
   */
  const saveIndividualValueForBusiness = async (field, value, name) => {
    try {
      field = mapFieldValueToBusiness(field);
      const data = {
        field,
        value,
        name,
      };

      if (field === 'statementDescriptor' && selectedConnectAccountId) {
        await updateStatementDescriptor({ id: selectedConnectAccountId, value });
        await refetchBusiness();
        await refetchPayoutAccounts();
        setSelectedConnectAccountId(null);
      } else {
        await updateIndividualValueForBusiness(id, data);
        await refetchBusiness();
      }

      setShowBusinessDetailsModal(false);
      setShowBusinessTypeModal(false);
      setShowEditMessengerConfigurationModal(false);
      setShowDudaConfigurationModal(false);
    } catch (error) {
      setFormError(error?.response?.data?.error || THEME_MESSAGES.somethingWrong);
    }
  };

  /**
   * Call the API to accept the terms of service on behalf of the stripe account
   *
   * @param {string} accountId
   */
  const acceptTermsOfServiceForPayoutAccount = async (accountId) => {
    if (acceptingTosAccountIds.includes(accountId)) {
      return;
    }

    setAcceptingTosAccountIds((ids) => [...ids, accountId]);
    try {
      await acceptStripeTermsOfService({ accountId });
      await refetchPayoutAccounts();

      toaster('Stripe Terms of Service updated!', {
        icon: '✅',
      });
    } catch (error) {
      // use toaster to display the error message instead of relying on formError
      // to not skip repeated identical failures
      toaster(error?.response?.data?.error || THEME_MESSAGES.somethingWrong, {
        icon: '❌',
      });
    } finally {
      setAcceptingTosAccountIds((ids) => ids.filter((id) => id !== accountId));
    }
  };

  const editBusinessTheme = () => {
    setInitialEditableCertainTheme(business?.businessTheme);
  };

  const resetChurnTypeSelection = (value, name) => {
    if (name === 'Type' && value !== 'CHURN') {
      setChurnTypeConfirmed(false);
    }
  };

  /**
   * Set the form value to be updated for the Business
   *
   * @param {Any} value
   * @param {String} name
   */
  const handleBusinessValueChange = (value, name) => {
    setFormError(null);
    checkIsValueValid(value, name);
    resetChurnTypeSelection(value, name);
    setUpdatedFormValue(value);
  };

  /**
   * Determine if the business type selection is valid to be submitted
   */
  const isBusinessTypeInvalid = () => {
    if (updatedFormValue === 'CHURN' && updatedFormKey === 'Type' && !churnTypeConfirmed) {
      return true;
    }

    if (updatedFormValue && updatedFormKey === 'Type') {
      return false;
    }

    return true;
  };

  /**
   * Map the editable field names to valid key values in the business model
   * @param {Any} value
   *
   */
  const mapFieldValueToBusiness = (field) => {
    const mapping = {
      'Business Email': 'businessOwnerEmail',
      'Business Name': 'name',
      'Business Owner': 'businessOwnerName',
      Address: 'address',
      City: 'city',
      'Zip Code': 'zipCode',
      State: 'state',
      'Stripe Connect ID': 'merchantId',
      'Stripe Statement Descriptor': 'statementDescriptor',
      Type: 'type',
    };
    return mapping[field];
  };

  // The flag can turn off while this tab is open; fall back to Stores rather than a blank page.
  useEffect(() => {
    if (!entitlementsProvisioning && selectedTab === TABS_VALUES.entitlements) {
      setSelectedTab(TABS_VALUES.stores);
    }
  }, [entitlementsProvisioning, selectedTab]);

  useEffect(() => {
    if (error) {
      toaster(error, {
        icon: '❌',
      });
    }
  }, [error]);

  /**
   * Determine which "editing" modal to show based on the modalType
   *
   * @param {String} incomingModalType
   */
  const handleEditBusinessDetails = (incomingModalType) => {
    setModalType(incomingModalType);
    setUpdatedFormKey(incomingModalType);

    switch (incomingModalType) {
      case BUSINESS_MODAL_TYPES.type:
        setShowBusinessTypeModal(true);
        break;
      case BUSINESS_MODAL_TYPES.messengerConfiguration:
        setShowEditMessengerConfigurationModal(true);
        break;
      default:
        setShowBusinessDetailsModal(true);
    }
  };

  /**
   * Render the core business details
   */
  const renderMainDetails = () => {
    const storeDetails = [
      {
        label: 'Business Name',
        data: business.name,
        link: false,
        editable: true,
      },
      {
        label: 'Business Owner',
        data: business.businessOwnerName || `${business.user.firstname} ${business.user.lastname}`,
        link: false,
      },
      {
        label: 'Business Email',
        data: business.businessOwnerEmail || business.user.email,
        id: business.userId || business.user.id,
        link: true,
        route: 'users',
      },
      {
        label: 'Address',
        data: business.address,
        link: false,
        editable: true,
      },
      {
        label: 'City',
        data: business.city,
        link: false,
        editable: true,
      },
      {
        label: 'State',
        data: business.state,
        link: false,
        editable: true,
      },
      {
        label: 'Zip Code',
        data: business.zipCode,
        link: false,
        editable: true,
      },
      {
        label: 'Type',
        data: business.type,
        link: false,
        editable: true,
      },
      {
        label: THEME_ELEMENTS.businessLabel,
        data: (
          <Button variant="outlined" color="primary" onClick={editBusinessTheme}>
            {BUTTONS.edit}
          </Button>
        ),
        rawLabel: 'businessThemes',
        toggleable: false,
      },
      {
        label: 'Messenger Configuration',
        rawLabel: BUSINESS_MODAL_TYPES.messengerConfiguration,
        data: (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setShowEditMessengerConfigurationModal(true)}
          >
            {BUTTONS.edit}
          </Button>
        ),
      },
      {
        label: 'Duda Configuration',
        data: (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setShowDudaConfigurationModal(true)}
          >
            {BUTTONS.edit}
          </Button>
        ),
        rawLabel: 'dudaConfiguration',
      },
    ];

    if (business?.settings?.dataSharingConsentDate && business?.settings?.isDataSharingConsented) {
      storeDetails.push({
        label: 'Generate experimental insights',
        rawLabel: BUSINESS_MODAL_TYPES.type,
        data: (
          <Button variant="outlined" color="primary" onClick={generatePredictiveInsights}>
            {BUTTONS.yes}
          </Button>
        ),
      });
    }

    return (
      <DetailsCard itemsPerColumn={4} cardData={storeDetails} onEdit={handleEditBusinessDetails} />
    );
  };

  /**
   * Go to the individual store profile
   *
   * @param {Number} storeId
   */
  const navigateToIndividualStoreProfile = (storeId) => {
    history.push(`/stores/${storeId}`);
  };

  /**
   * Copy the payout account ID to clipboard for ease of use
   *
   * @param {string} text
   */
  const copyTextToClipboard = async (text) => {
    toaster('Stripe Connect Account ID copied!', {
      icon: '✅',
    });
    if ('clipboard' in navigator) {
      return navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text); // for browsers that don't support clipboard API
    }
  };

  /**
   * 1) Set the incoming payout account ID in state,
   * 2) Show the modal to edit statement descriptor
   * 3) Copy the payout account ID to clipboard
   *
   * @param {object} rowEntry
   */
  const handlePayoutAccountRowSelection = (rowEntry) => {
    setSelectedConnectAccountId(rowEntry?.accountId);
    handleEditBusinessDetails('Stripe Statement Descriptor');
  };

  const businessThemeLink =
    business?.businessTheme?.customUrl || business?.businessTheme?.encodedLink;

  /**
   * Render the Stores data
   */
  const renderTabs = () => {
    const storeThemeCell = ({ value, row }) => {
      const liveLinkUrl = `${LIVELINK_ORDER_PAGE}${businessThemeLink}/${
        value?.customUrl || value?.encodedLink
      }`;

      const styledTheme = createTheme({
        shape: {
          borderRadius: value?.borderRadius,
        },
        palette: { primary: { main: value?.primaryColor.trim() } },
      });

      return (
        <ThemeProvider theme={styledTheme}>
          <div className="store-theme-cell">
            <Button
              color="primary"
              size="small"
              variant="contained"
              onClick={(event) => {
                event.stopPropagation();
                setInitialEditableCertainTheme(row.theme);
              }}
              classes={{ root: 'store-theme-cell-button' }}
            >
              {BUTTONS.edit}
            </Button>
            <Button
              color="primary"
              size="small"
              variant="contained"
              href={liveLinkUrl}
              target="_blank"
              onClick={(event) => {
                event.stopPropagation();
              }}
              classes={{ root: 'store-theme-cell-button' }}
            >
              {BUTTONS.liveLink}
            </Button>
          </div>
        </ThemeProvider>
      );
    };

    const storesGridColumDefs = [
      { headerName: 'ID', field: 'id', flex: 0.5 },
      { headerName: 'Name', field: 'name', flex: 1 },
      { headerName: 'Phone Number', field: 'phoneNumber', flex: 1 },
      { headerName: 'Location Branding', field: 'theme', renderCell: storeThemeCell, flex: 1.5 },
      { headerName: 'Address', field: 'address', flex: 1 },
      { headerName: 'City', field: 'city', flex: 1 },
      { headerName: 'State', field: 'state', flex: 1 },
      { headerName: 'Zip Code', field: 'zipCode', flex: 0.5 },
    ];
    const payoutAccountsGridColumDefs = [
      { headerName: 'ID', field: 'id', flex: 0.5 },
      { headerName: 'Connect ID', field: 'accountId', flex: 0.5 },
      { headerName: 'Account Name', field: 'accountName', flex: 1 },
      { headerName: 'Last 4', field: 'accountLast4', flex: 1 },
      { headerName: 'Default', field: 'default', flex: 1 },
      { headerName: 'Card Payments Status', field: 'cardPayments', flex: 1 },
      { headerName: 'Transfers Status', field: 'transfers', flex: 1 },
      { headerName: 'ACH Payments Status', field: 'achPayments', flex: 1 },
      {
        headerName: 'Statement Descriptor',
        field: 'statementDescriptor',
        flex: 1,
        renderCell: ({ row }) => (
          <p
            className="highlighted-text"
            onClick={() => {
              handlePayoutAccountRowSelection(row);
            }}
          >
            {row.statementDescriptor}
          </p>
        ),
      },
      {
        headerName: 'Accept Terms of Service',
        flex: 1,
        renderCell: ({ row: { accountId } }) =>
          acceptingTosAccountIds.includes(accountId) ? (
            <CircularProgress size={16} />
          ) : (
            <p
              className="highlighted-text"
              onClick={() => {
                acceptTermsOfServiceForPayoutAccount(accountId);
              }}
            >
              Accept Terms of Service
            </p>
          ),
      },
    ];

    return (
      <>
        <Tabs
          tabsData={
            entitlementsProvisioning
              ? TABS_DATA
              : TABS_DATA.filter(({ value }) => value !== 'entitlements')
          }
          tabValueSelected={selectedTab}
          handleTabChange={(event, newValue) => {
            setSelectedTab(newValue);
          }}
        />
        {selectedTab === TABS_VALUES.stores && (
          <CentsDataGrid
            tableName="STORES_FOR_INDIVIDUAL_BUSINESS"
            data={storeList ?? []}
            columns={storesGridColumDefs}
            isLoading={loading}
            checkboxSelection
            disableSelectionOnClick
            onRowClick={({ id }) => navigateToIndividualStoreProfile(id)}
            onRowSelectionModelChange={(selectionModel) => {
              setSelectedStores(selectionModel);
            }}
          />
        )}
        {selectedTab === TABS_VALUES.businessSettings && (
          <IndividualBusinessSettings storeList={storeList} />
        )}
        {selectedTab === TABS_VALUES.featureFlags && <IndividualBusinessFeatureFlags />}
        {selectedTab === TABS_VALUES.gmvData && <GMVData businessId={business.id} />}
        {selectedTab === TABS_VALUES.devices && (
          <BusinessDevices
            stores={storeList.map((store) => {
              return { id: store.id, name: store.name };
            })}
          />
        )}
        {selectedTab === TABS_VALUES.payoutAccounts && (
          <>
            <Box className="payout-accounts-actions">
              <Button
                variant="contained"
                color="primary"
                disabled={
                  !selectedPayoutAccounts.length ||
                  isArchivingPayoutAccounts ||
                  payoutAccounts.length === selectedPayoutAccounts.length
                }
                onClick={() => {
                  archivePayoutAccounts({
                    businessId: business.id,
                    payoutAccountIds: selectedPayoutAccounts,
                  });
                }}
              >
                Archive
              </Button>
            </Box>
            <CentsDataGrid
              tableName="PAYOUT_ACCOUNTS_FOR_INDIVIDUAL_BUSINESS"
              data={payoutAccounts ?? []}
              columns={payoutAccountsGridColumDefs}
              isLoading={loading}
              checkboxSelection
              disableSelectionOnClick
              onRowSelectionModelChange={(selectionModel) => {
                setSelectedPayoutAccounts(selectionModel);
              }}
              onRowClick={async (rowEntry) => {
                await copyTextToClipboard(rowEntry?.row?.accountId);
              }}
            />
          </>
        )}
        {selectedTab === TABS_VALUES.payrocMerchantAccounts && (
          <MerchantAccounts businessId={business.id} />
        )}
        {selectedTab === TABS_VALUES.paypalMerchantAccounts && (
          <PayPalMerchantAccounts businessId={business.id} />
        )}
        {selectedTab === TABS_VALUES.webhookEndpoints && (
          <WebhookEndpoints businessId={business.id} />
        )}
        {selectedTab === TABS_VALUES.centsAssist && <CentsAssist businessId={business.id} />}

        {selectedTab === TABS_VALUES.twilioIntegration && (
          <TwilioIntegration
            businessId={business.id}
            businessName={business.name}
            businessWebsiteUrl={business.website ?? ''}
          />
        )}

        {selectedTab === TABS_VALUES.billing && <BillingConfiguration businessId={business.id} />}

        {selectedTab === TABS_VALUES.laundroworksIntegration && (
          <LaundroworksIntegration businessId={business.id} businessName={business.name} />
        )}

        {entitlementsProvisioning && selectedTab === TABS_VALUES.entitlements && (
          <Entitlements key={business.id} businessId={business.id} stores={storeList} />
        )}
      </>
    );
  };

  /**
   * Show the edit-details modal
   */
  const renderModal = () => {
    const field = mapFieldValueToBusiness(modalType);
    const currentValue = business[field];
    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowBusinessDetailsModal(false);
          setUpdatedFormKey(null);
          setUpdatedFormValue(null);
        }}
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={currentValue}
          updatedValue={updatedFormValue}
          handleValueChange={(value) => handleBusinessValueChange(value, modalType)}
          inputType="text"
          onSave={() => {
            saveIndividualValueForBusiness(updatedFormKey, updatedFormValue, business.name);
          }}
          error={formError}
        />
      </Modal>
    );
  };

  const handleBusinessTypeSelection = async (formKey, formValue, businessName) => {
    // if the business is currently churned but we're changing to live, we need to mark it as a churn return
    if (business.type === BUSINESS_TYPE_VALUES.CHURN && formValue === BUSINESS_TYPE_VALUES.LIVE) {
      await markBusinessAsChurnReturn({ businessId: id });
      return setShowBusinessTypeModal(false);
    }

    // if the incoming value is churn, no matter what, call the churn API
    if (formValue === BUSINESS_TYPE_VALUES.CHURN) {
      await markBusinessAsChurned({ businessId: id });
      return setShowBusinessTypeModal(false);
    }

    // if we're marking a business from test to live, live to test, churn to test, etc. we just update the type
    return saveIndividualValueForBusiness(formKey, formValue, businessName);
  };

  const handleChurnTypeConfirmationCheckboxChange = (event) => {
    setChurnTypeConfirmed(event.target.checked);
  };

  /**
   * Show the modal to update the "type" value for a business
   */
  const renderTypeModal = () => {
    return (
      <Modal
        onClose={() => {
          setModalType(null);
          setShowBusinessTypeModal(false);
        }}
        modalHeight="75%"
      >
        <IndividualValueEditForm
          modalType={modalType}
          currentValue={business.type}
          updatedValue={updatedFormValue}
          handleValueChange={(value) => handleBusinessValueChange(value?.value, modalType)}
          inputType="select"
          selectOptions={businessTypes}
          onSave={() => {
            handleBusinessTypeSelection(updatedFormKey, updatedFormValue, business.name);
          }}
          error={formError}
          isSubmitDisabled={isBusinessTypeInvalid()}
          showConfirmationCheckbox={updatedFormValue === 'CHURN' && updatedFormKey === 'Type'}
          handleConfirmationCheckboxChange={handleChurnTypeConfirmationCheckboxChange}
          confirmCheckboxValue={churnTypeConfirmed}
        />
      </Modal>
    );
  };

  const renderEditMessengerConfigurationModal = () => {
    const onClose = () => {
      setModalType(null);
      setShowEditMessengerConfigurationModal(false);
    };

    return (
      <Modal onClose={onClose} modalHeight={'auto'}>
        <EditMessengerConfiguration businessId={business.id} onClose={onClose} />
      </Modal>
    );
  };

  const renderDudaConfigurationModal = () => {
    const onClose = () => {
      setModalType(null);
      setShowDudaConfigurationModal(false);
    };

    return (
      <Modal onClose={onClose} modalHeight={'auto'}>
        <EditDudaConfiguration businessId={business.id} onClose={onClose} />
      </Modal>
    );
  };

  const { mutateAsync: impersonateUser } = useImpersonateUser();

  return (
    <>
      {loading ||
      isFetchingPredictiveInsights ||
      isMarkingBusinessAsChurned ||
      isMarkingBusinessAsChurnReturn ? (
        <CentsLoader />
      ) : isNotFound ? (
        <NotFound message={error} />
      ) : (
        <div className="page-container">
          {business && (
            <>
              <div className="business-page-header">
                <div className="business-page-header-left-item">
                  <div>Business Name: {business.name}</div>
                  <div className="business-page-header-small-item">
                    <span>(id: </span>
                    <span className="font-italic">{business.id}</span>
                    <span>)</span>
                  </div>
                </div>

                <div className="business-page-actions">
                  {business.source === RecordSource.LAUNDROWORKS &&
                    [LwSubscriptionStatus.PROVISIONAL, LwSubscriptionStatus.SUSPENDED].includes(
                      business.subscriptionStatus,
                    ) && (
                      <button
                        className="submit-button"
                        onClick={() => setIsActivateBusinessModalOpen(true)}
                      >
                        {BUTTONS.activate}
                      </button>
                    )}

                  {businessImpersonate && (
                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={() => impersonateUser({ userId: business.userId })}
                    >
                      {BUTTONS.impersonate}
                    </Button>
                  )}
                </div>
              </div>
              {renderMainDetails()}
              {renderTabs()}
              {showBusinessDetailsModal && renderModal()}
              {showBusinessTypeModal && renderTypeModal()}
              {showEditMessengerConfigurationModal && renderEditMessengerConfigurationModal()}
              {showDudaConfigurationModal && renderDudaConfigurationModal()}
              <ActivateBusinessModal
                isOpen={isActivateBusinessModalOpen}
                onClose={() => setIsActivateBusinessModalOpen(false)}
                business={business}
                onSaveSuccess={refetchAllData}
              />
            </>
          )}
        </div>
      )}
      <ThemeContainer
        initialEditableCertainTheme={initialEditableCertainTheme}
        setInitialEditableCertainTheme={setInitialEditableCertainTheme}
        businessId={id}
        businessThemeLink={businessThemeLink}
        selectedStores={selectedStoreData}
        refreshStoreAndBusiness={refreshStoreAndBusiness}
        setSelectedStores={setSelectedStores}
      />
    </>
  );
};

export default IndividualBusinessProfile;
